import {
  CompositeDecorator,
  ContentBlock,
  ContentState,
  EditorState,
} from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { ReactNode } from "react";
import ReactDOMServer from "react-dom/server";

import {
  buildHtmlForBlockText,
  getClassesAndStyles,
  getStateToHtmlOptions,
} from "@/models/editors/draft-js-rte/renderConfig";
import { TemplatePart } from "@/states/page-editor/template-parts/types";

import { FormCustomFormCustomFormsDescriptionJson } from "@/apis/clients";
import { isEmpty, isObject } from "lodash";
import { convertFromRawRecursively } from "@/utils/convert-page-json";
/**
 * 空のコンテンツを作成
 */
export function createEmptyContent() {
  return EditorState.createEmpty().getCurrentContent();
}

/**
 * コンテンツをHTML文字列に変換
 */
export function contentToHtml(content: ContentState): string {
  const options = getStateToHtmlOptions(content);
  return content.hasText() ? stateToHTML(content, options) : "";
}

/**
 * Converts the given ContentState to HTML with custom style and class.
 * @param content The ContentState to convert.
 * @param customStyle The custom style to apply to the unstyled blocks.
 * @param customClass The custom class to apply to the unstyled blocks.
 * @returns The HTML representation of the ContentState.
 */
export function contentToHtmlWithCustomStyle(
  content: ContentState,
  customClass?: string,
  customStyle?: string,
): string {
  let options = getStateToHtmlOptions(content, customClass, customStyle);
  options = {
    ...options,
    blockRenderers: {
      unstyled: (block) => {
        if (block.getLength() === 0) {
          return `<div${getClassesAndStyles({
            block,
          })}><br></div>`;
        }
        // get block-level styling and classes if any
        // "result" will be the html eventually returned from this function
        const result = `<div${getClassesAndStyles({
          block,
        })}><div ${customStyle ? `style="${customStyle}"` : ""} ${
          customClass ? `class="${customClass}"` : ""
        }>${buildHtmlForBlockText("", block, content)}</div></div>`;
        return result;
      },
    },
  };
  return content.hasText() ? stateToHTML(content, options) : "";
}

/**
 * 画像テンプレートに設定がない場合のデフォルト画像(サンクスページ)
 */
export const defaultThanksImagePath = "/customer/img/thanks_page.png";

export const noImagePath = "/customer/img/no_image.svg";

/**
 * Link Decoratorを適用する範囲を決定する
 */
function findLinkEntities(
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState,
) {
  contentBlock.findEntityRanges((character) => {
    // ContentBlockの内のテキスト各文字に関連付けられたメタデータからEntityのキーを取り出す
    const entityKey = character.getEntity();
    return (
      entityKey != null &&
      contentState.getEntity(entityKey).getType() === "LINK"
    );
  }, callback);
}

/**
 * リンク描画を行う
 */
function LinkRenderer({
  contentState,
  entityKey,
  children,
}: {
  contentState: ContentState;
  entityKey: string;
  children: ReactNode;
}) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { url, target }: { url?: string; target?: string } = contentState
    .getEntity(entityKey)
    .getData();

  if (url != null) {
    return (
      <a href={url} target={target ?? ""}>
        {children}
      </a>
    );
  }
}

/**
 * Link Decoratorを生成
 */
export function makeLinkDecorator() {
  return new CompositeDecorator([
    {
      strategy: findLinkEntities,
      component: LinkRenderer,
    },
  ]);
}

export function getTemplatePartArray(state: EditorState) {
  if (!state) return [];

  return state
    .getCurrentContent()
    .getBlocksAsArray()
    .filter((element) => element.getType() === "atomic")
    .map((element) => {
      const part = element.getData().get("TEMPLATE") as TemplatePart;
      return part;
    });
}

/**
 * Extract TemplatePart[] from raw Draft.js content state without creating EditorState.
 * This is a lightweight version of getTemplatePartArray for server-side processing.
 * 
 * @param rawContentState - Raw Draft.js content state object (from page_editor_content.draftContentState)
 * @returns Array of TemplatePart objects
 */
export function getTemplatePartArrayFromRaw(rawContentState: unknown): TemplatePart[] {
  if (!rawContentState || typeof rawContentState !== 'object') {
    return [];
  }

  // Type guard for RawDraftContentState
  const content = rawContentState as Record<string, unknown>;
  
  if (!content.blocks || !Array.isArray(content.blocks)) {
    return [];
  }

  const templateParts: TemplatePart[] = [];

  for (const block of content.blocks) {
    if (!block || typeof block !== 'object') continue;

    const blockData = block as Record<string, unknown>;
    
    // Only process atomic blocks (template parts)
    if (blockData.type !== 'atomic') continue;

    // Try to extract TEMPLATE data from block.data first
    if (blockData.data && typeof blockData.data === 'object') {
      const dataObj = blockData.data as Record<string, unknown>;
      
      // Check if TEMPLATE data is directly in block.data
      if (dataObj.TEMPLATE) {
        templateParts.push(dataObj.TEMPLATE as TemplatePart);
        continue;
      }
    }

    // Alternative: Try to get from entityMap via entityRanges
    if (blockData.entityRanges && Array.isArray(blockData.entityRanges)) {
      const entityRanges = blockData.entityRanges as Array<Record<string, unknown>>;
      
      if (entityRanges.length > 0) {
        const firstEntityKey = entityRanges[0]?.key;
        
        if (typeof firstEntityKey === 'number' && content.entityMap) {
          const entityMap = content.entityMap as Record<string, unknown>;
          const entityKeyStr = String(firstEntityKey);
          const entity = entityMap[entityKeyStr] as Record<string, unknown> | undefined;
          
          if (entity?.type === 'TEMPLATE' && entity.data) {
            templateParts.push(entity.data as TemplatePart);
          }
        }
      }
    }
  }

  return templateParts;
}

/**
 * @functionName contentJsonAsHtml
 * @param contentJson
 */
export function contentJsonAsHtml(contentJson?: FormCustomFormCustomFormsDescriptionJson): string {
  const contentState: ContentState|null = (!isEmpty(contentJson) && contentJson?.content !== undefined && isObject(contentJson?.content) && !isEmpty((contentJson?.content))) ? convertFromRawRecursively(Object(contentJson?.content) as object) : null;
  if (contentState) {
    const htmlDom: string = ReactDOMServer.renderToString(
      <div
        style={contentJson?.textAreaStyle ? {
          ...contentJson?.textAreaStyle,
          height: undefined,
          lineHeight: "120%"
        } : {lineHeight: "120%"}}
        dangerouslySetInnerHTML={{__html: contentToHtml(contentState)}}
        className={"flex flex-col max-w-full break-word my-2"}
      />
    );
    return htmlDom;
  }
  return '';
}
